import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useAuth } from "../../context/AuthContext";
import { useDocumentMeta } from "../../hooks/useDocumentMeta";
import Logo from "../../components/Logo";
import { useBranding } from "../../context/BrandingContext";

interface IFormInput {
  email: string;
  password: string;
}

const schema = yup.object({
  email: yup.string().email("Email inválido").required("Email é obrigatório"),
  password: yup.string().required("Senha é obrigatória"),
});

const Login: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { signIn } = useAuth();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { branding } = useBranding();

  // Atualiza o título e favicon da página
  useDocumentMeta();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<IFormInput>({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: IFormInput) => {
    try {
      setLoading(true);
      setError("");
      await signIn(data.email, data.password);
      navigate("/dashboard");
    } catch (err: any) {
      setError(err.response?.data?.message || "Erro ao fazer login");
    } finally {
      setLoading(false);
    }
  };

  const backgroundGradient = `linear-gradient(to right, ${branding.colors.sidebarStart}, ${branding.colors.sidebarEnd})`;
  const borderGradient = `linear-gradient(to top, ${branding.colors.primary}, rgba(0, 0, 0, 0)) 1 100%`;
  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: backgroundGradient,
        p: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          height: "100vh",
          position: "relative",
          justifyContent: "center",
          borderWidth: "3px",
          borderStyle: "solid",
          borderColor: "initial",
          borderRadius: "11px",
          borderImage: borderGradient,
          sm: { width: "90%" },
          md: {
            width: "40%",
          },
          lg: {
            width: "35%",
          },
          xl: {
            width: "30%",
          },
        }}
      >
        <CardContent
          sx={{
            p: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <Logo height={48} />
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit(onSubmit)}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              margin="normal"
              {...register("email")}
              error={!!errors.email}
              helperText={errors.email?.message}
              InputProps={{
                sx: {
                  color: branding.colors.textDark,
                  backgroundColor: branding.colors.background,
                  borderRadius: "11px",
                  border: `1px solid ${branding.colors.primary}`,
                },
              }}
            />

            <TextField
              fullWidth
              label="Senha"
              type="password"
              margin="normal"
              {...register("password")}
              error={!!errors.password}
              helperText={errors.password?.message}
              InputProps={{
                sx: {
                  color: branding.colors.textDark,
                  backgroundColor: branding.colors.background,
                  borderRadius: "11px",
                  border: `1px solid ${branding.colors.primary}`,
                },
              }}
            />

            <Button
              fullWidth
              variant="contained"
              type="submit"
              size="large"
              disabled={loading}
              sx={{ mt: 3, mb: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : "Entrar"}
            </Button>
          </form>

          <Box sx={{ textAlign: "center" }}>
            <Link component={RouterLink} to="/forgot-password" variant="body2">
              Esqueceu sua senha?
            </Link>
          </Box>

          <Box sx={{ textAlign: "center", mt: 2 }}>
            <Typography variant="body2" color={theme.palette.common.white}>
              Não tem conta?{" "}
              <Link component={RouterLink} to="/signup">
                Cadastre-se
              </Link>
            </Typography>
          </Box>
        </CardContent>
      </Box>
    </Box>
  );
};

export default Login;
